import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { PatientService } from 'src/services/patient.service'

const useSmsDocument = (projectId: number, patientId: number, smsLogId: number, enabled: boolean) => {
  const queryClient = useQueryClient()

  const { isLoading, isError, error, data, refetch } = useQuery({
    queryKey: ['sms-document', projectId, patientId, smsLogId],
    queryFn: () => PatientService.getSmsDocument(projectId, patientId, smsLogId),
    enabled,
    // Retry a few times with exponential backoff: the inbound RCS media upload
    // runs asynchronously after the SmsLog is created, so the document endpoint
    // may return 404 for a short window after the NEW_SMS websocket event fires.
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
  })

  // Once all retries are exhausted, invalidate the SMS list so the parent
  // re-fetches each SmsLog with its final type from the server.
  useEffect(() => {
    if (isError && !isLoading) {
      queryClient.invalidateQueries({ queryKey: ['patient-sms', projectId, patientId] })
    }
  }, [isError, isLoading, queryClient, projectId, patientId])

  const objectUrl = useMemo(() => {
    if (!data?.data) return undefined
    return URL.createObjectURL(data.data as Blob)
  }, [data?.data])

  // Derive mimeType from the Content-Type response header.
  const mimeType = useMemo((): string => {
    const contentType = data?.headers?.['content-type'] as string | undefined
    return contentType?.split(';')[0]?.trim() ?? 'application/octet-stream'
  }, [data?.headers])

  return { isLoading, isError, error, objectUrl, mimeType, refetch }
}

export { useSmsDocument }
